// ============================================================================
// DW Squad Creator Portal — entry point.
// ============================================================================

// First import on purpose: it may leave this origin entirely, and it must run
// before lib/supabase.js can consume auth tokens into the wrong localStorage.
import './lib/canonical-host.js';

import './styles/dw-tokens.css';
import './styles/dw-ui.css';

import { initRouter } from './router.js';
import { initGlobalMotion } from './lib/motion.js';
import { initLoader, setProgress, hideLoader, seedParticles } from './lib/loader.js';
import { toastError } from './lib/ui.js';

initLoader();
seedParticles();
setProgress(0.25);   // modules parsed

// A missing Supabase env var throws at import time inside lib/supabase.js,
// which would otherwise leave a blank white page and a console-only message.
initRouter().then(() => {
  // Must run AFTER the router mounts the header: initGlobalMotion binds to
  // .dw-header, which does not exist until then. Running it first silently
  // did nothing.
  initGlobalMotion();
  setProgress(1);
  hideLoader();
}).catch((err) => {
  console.error('[boot]', err);
  // Force the splash away: leaving it up would hide the very error the user
  // needs to see behind a green screen that never finishes.
  hideLoader(true);
  const app = document.getElementById('app');
  if (app) {
    app.innerHTML = `
      <div class="dw-container dw-section">
        <div class="dw-panel dw-panel--pad">
          <h1 class="dw-h4">The portal could not start</h1>
          <p class="dw-muted">${String(err?.message ?? err).replace(/[<>&]/g, '')}</p>
        </div>
      </div>`;
  }
});

window.addEventListener('unhandledrejection', (e) => {
  console.error('[unhandled]', e.reason);
  toastError(e.reason?.message ?? 'Something went wrong.');
});

// ---------------------------------------------------------------------------
// Dev-only layout guard.
//
// Horizontal overflow is nearly invisible in source and obvious on screen: one
// element a few pixels too wide makes the whole document scrollable, which
// shows as a strip of page background beside every full-width dark section.
// Rather than hunt for it by eye, have the page name the culprit.
// Stripped from production builds.
// ---------------------------------------------------------------------------
if (import.meta.env.DEV) {
  const findOverflow = () => {
    const limit = document.documentElement.clientWidth;
    const guilty = [];

    for (const el of document.querySelectorAll('body *')) {
      const rect = el.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) continue;
      // Ignore anything deliberately clipped by an ancestor (the marquee).
      if (el.closest('.dw-marquee, [style*="overflow"]')) continue;
      if (rect.right > limit + 1 || rect.left < -1) {
        guilty.push({
          el,
          selector: el.tagName.toLowerCase() +
            (el.className && typeof el.className === 'string'
              ? '.' + el.className.trim().split(/\s+/).slice(0, 3).join('.')
              : ''),
          overflowRight: Math.round(rect.right - limit),
          overflowLeft: Math.round(-rect.left)
        });
      }
    }

    if (guilty.length) {
      console.warn(
        `[layout] ${guilty.length} element(s) overflow the viewport ` +
        `(${limit}px). This is what shows as a strip beside dark sections:`
      );
      console.table(guilty.map(({ selector, overflowRight, overflowLeft }) =>
        ({ selector, overflowRight, overflowLeft })));
      console.log('Elements:', guilty.map((g) => g.el));
    } else {
      console.log(`[layout] no horizontal overflow at ${limit}px`);
    }
  };

  window.addEventListener('hashchange', () => setTimeout(findOverflow, 400));
  window.addEventListener('resize', () => {
    clearTimeout(window.__dwOverflowTimer);
    window.__dwOverflowTimer = setTimeout(findOverflow, 300);
  });
  setTimeout(findOverflow, 900);
}
